import { InputNumber } from 'antd';
import React from 'react';
import { Section } from '../../../components/form-fields/section';
import { Doc31RuleSettings } from '../../../types/doc31';
import {
    describeDoc31Path,
    DOC31_MAX_PLAY_DAYS,
    doc31FieldId,
} from '../../../utils/doc31';
import { SetupField, SetupFormApi } from './fields';
import style from './style.module.scss';

type CountRule = Exclude<
    keyof Doc31RuleSettings,
    'instantWinProbability' | 'enabledCapabilities'
>;

const COUNT_RULES: {
    field: CountRule;
    min: number;
    max?: number;
    placeholder?: string;
    helperText?: string;
    addonAfter?: string;
}[] = [
    {
        field: 'maxPlayDays',
        min: 1,
        max: DOC31_MAX_PLAY_DAYS,
        placeholder: '31',
        helperText: 'Doors in the calendar. Follows the play dates.',
    },
    { field: 'baseEntriesPerPlay', min: 0 },
    { field: 'maxPlaysPerLocalDate', min: 1, helperText: '31DOC allows one' },
    { field: 'dailyRedemptionLimit', min: 0 },
    { field: 'referralClaimWindowHours', min: 0, addonAfter: 'hours' },
    // Minimum age is hidden for now: a rules save keeps its stored value.
];

const WIN_CHANCE = 'rules.instantWinProbability';

export const RulesSection = ({
    form,
    onMaxPlayDaysChange,
    saveAction,
}: {
    form: SetupFormApi;
    /** Unsaved Play days, for the page header's overflow check. */
    onMaxPlayDaysChange: (days: number | null) => void;
    /** The section's own save, on its title row. */
    saveAction: React.ReactNode;
}) => {
    const { rules } = form.values;
    // Shown as a percentage, stored as a probability (0–1).
    const winChance =
        rules.instantWinProbability === null
            ? null
            : Math.round(rules.instantWinProbability * 10000) / 100;

    return (
        <Section
            id={doc31FieldId('rules')}
            title="Game rules"
            headerActions={saveAction}
        >
            <div className={style.grid}>
                {COUNT_RULES.map(
                    ({
                        field,
                        min,
                        max,
                        placeholder,
                        helperText,
                        addonAfter,
                    }) => {
                        const path = `rules.${field}`;
                        return (
                            <SetupField
                                key={field}
                                path={path}
                                label={describeDoc31Path(path)}
                                helperText={helperText}
                            >
                                <InputNumber
                                    value={rules[field]}
                                    min={min}
                                    max={max}
                                    placeholder={placeholder}
                                    precision={0}
                                    addonAfter={addonAfter}
                                    disabled={form.readOnly}
                                    onChange={(value) => {
                                        form.setValue(path, value ?? null);
                                        if (field === 'maxPlayDays') {
                                            onMaxPlayDaysChange(value ?? null);
                                        }
                                    }}
                                />
                            </SetupField>
                        );
                    }
                )}
                <SetupField
                    path={WIN_CHANCE}
                    label={describeDoc31Path(WIN_CHANCE)}
                >
                    <InputNumber
                        value={winChance}
                        min={0}
                        max={100}
                        addonAfter="%"
                        disabled={form.readOnly}
                        onChange={(value) =>
                            form.setValue(
                                WIN_CHANCE,
                                value === null
                                    ? null
                                    : Number((value / 100).toFixed(6))
                            )
                        }
                    />
                </SetupField>
            </div>
        </Section>
    );
};
